/**
 * Frontend error capture. Three entry points are wired by `installErrorLogger()`:
 *
 *   1. window.onerror — synchronous JS errors
 *   2. window.onunhandledrejection — async / promise errors
 *   3. httpsCallable wrapper — every Cloud Function failure
 *
 * Plus a manual `logError()` for explicit places (try/catch handlers).
 *
 * All writes are best-effort — a failure in the logger itself swallows
 * silently to avoid infinite loops.
 */

import type { ErrorLogEntry, BreadcrumbEntry } from '@rendimees24/shared-types';
import {
  doc,
  increment,
  runTransaction,
  serverTimestamp,
  setDoc,
  type Firestore,
} from 'firebase/firestore';
import {
  httpsCallable as fbHttpsCallable,
  type Functions,
  type HttpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions';

import { isStaff } from '../auth/isStaff';

import { stripUndefined } from '../lib/stripUndefined';

import { announceCallableSuccess } from './clientWorkSignal';
import { getFirebase } from './firebase';

const SENSITIVE_FIELD_KEYWORDS = [
  'password',
  'token',
  'secret',
  'apikey',
  'authorization',
  'creditcard',
  'cardnumber',
  'cvv',
  'pin',
];
const MAX_STRING_LENGTH = 5000;
const MAX_DEPTH = 5;
const MAX_BREADCRUMBS = 10;

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_FIELD_KEYWORDS.some((needle) => lower.includes(needle));
}

function sanitizeForLog(input: unknown, depth = 0): unknown {
  if (depth >= MAX_DEPTH) return '[truncated: depth]';
  if (input == null) return input;
  const t = typeof input;
  if (t === 'string') {
    return (input as string).length > MAX_STRING_LENGTH
      ? `${(input as string).slice(0, MAX_STRING_LENGTH)}…[truncated]`
      : input;
  }
  if (t === 'number' || t === 'boolean') return input;
  if (Array.isArray(input)) {
    return input.slice(0, 100).map((v) => sanitizeForLog(v, depth + 1));
  }
  if (t === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      // Drop undefined outright. The client Firestore SDK REJECTS a document
      // containing an undefined field (ignoreUndefinedProperties is set on the
      // server, not here), and logError swallows its own failures — so an
      // undefined anywhere in the payload silently prevented the very error it
      // caused from ever being recorded. That is exactly how a createSite
      // failure reached a user's screen and no /errors entry.
      if (v === undefined) continue;
      if (isSensitiveKey(k)) {
        out[k] = '[redacted]';
      } else {
        out[k] = sanitizeForLog(v, depth + 1);
      }
    }
    return out;
  }
  return String(input);
}

async function sha1(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-1', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

function hourBucket(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}`;
}

/* ─── Breadcrumb ring buffer ─────────────────────────────────────── */

const breadcrumbs: BreadcrumbEntry[] = [];

export function addBreadcrumb(kind: string, detail?: string): void {
  breadcrumbs.push({ at: new Date().toISOString(), kind, detail });
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.splice(0, breadcrumbs.length - MAX_BREADCRUMBS);
  }
}

function snapshotBreadcrumbs(): BreadcrumbEntry[] {
  return breadcrumbs.slice();
}

/* ─── Caller context ─────────────────────────────────────────────── */

interface CallerContext {
  uid?: string;
  role?: string;
}

let getCaller: () => CallerContext = () => ({});

export function setCallerContextProvider(fn: () => CallerContext): void {
  getCaller = fn;
}

/* ─── Direct write ───────────────────────────────────────────────── */

interface LogErrorInput {
  surface: string;
  message: string;
  stack?: string;
  errorCode?: string;
  severity?: ErrorLogEntry['severity'];
  callableName?: string;
  inputSnapshot?: unknown;
}

export async function logError(input: LogErrorInput): Promise<void> {
  try {
    const firstStackLine = (input.stack ?? '').split('\n').slice(0, 2).join('\n');
    const fingerprint = await sha1(`${input.surface}|${input.message}|${firstStackLine}`);
    const docId = `${fingerprint}_${hourBucket()}`;
    const { firestore } = getFirebase();
    const ref = doc(firestore as Firestore, 'errorLog', docId);
    const nowIso = new Date().toISOString();
    const caller = getCaller();
    const baseFields = {
      id: docId,
      fingerprint,
      source: 'frontend',
      severity: input.severity ?? 'error',
      message: input.message.slice(0, 1000),
      stack: input.stack?.slice(0, 8000) ?? null,
      errorCode: input.errorCode ?? null,
      surface: input.surface,
      callableName: input.callableName ?? null,
      route: typeof window !== 'undefined' ? window.location.pathname : null,
      userUid: caller.uid ?? null,
      userRole: caller.role ?? null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      inputSnapshot: sanitizeForLog(input.inputSnapshot ?? null) ?? null,
      breadcrumbs: snapshotBreadcrumbs(),
      status: 'open' as const,
    };

    // Only admin/finance/dispatcher can READ /errorLog, so a read-modify-write
    // transaction would 403 for workers/clients and spam the console. Those
    // roles use a read-free merge write (dedup by doc id + atomic increment).
    const canReadLog = isStaff(caller.role);

    if (canReadLog) {
      await runTransaction(firestore as Firestore, async (tx) => {
        const snap = await tx.get(ref);
        if (snap.exists()) {
          tx.update(ref, {
            occurrenceCount: (snap.data().occurrenceCount ?? 1) + 1,
            lastOccurredAt: nowIso,
          });
        } else {
          tx.set(ref, {
            ...baseFields,
            occurrenceCount: 1,
            firstOccurredAt: nowIso,
            lastOccurredAt: nowIso,
            createdAt: serverTimestamp(),
          });
        }
      });
    } else {
      await setDoc(
        ref,
        {
          ...baseFields,
          occurrenceCount: increment(1),
          firstOccurredAt: nowIso,
          lastOccurredAt: nowIso,
          createdAt: serverTimestamp(),
        },
        { merge: true },
      );
    }
  } catch {
    /* fail-safe — logger errors are swallowed */
  }
}

/* ─── Window-level error handlers ────────────────────────────────── */

export function installErrorLogger(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    // A throw inside a CROSS-ORIGIN script reaches us stripped: the browser
    // withholds the message, file, line and Error object as a security
    // boundary, leaving the literal string "Script error." and nothing else.
    // Our own bundle is same-origin and carries `crossorigin`, so this is
    // never our code — it is a third-party script (Google sign-in, Maps) or a
    // browser extension, and no amount of logging will make that entry
    // informative.
    //
    // Still worth recording, because a SPIKE is signal even when one instance
    // is not. But recorded as what it is: filed as a warning under its own
    // code, so it stops sitting in the fault list looking like an unsolved
    // crash and quietly teaching people that the log is full of noise.
    const opaque = event.message === 'Script error.' && !event.error && !event.filename;

    void logError({
      surface: window.location.pathname + (event.filename ? ` @ ${event.filename}` : ''),
      message: opaque
        ? 'Kolmanda osapoole skripti viga — brauser ei avalda detaile (cross-origin)'
        : (event.message ?? 'Unknown error'),
      stack: event.error?.stack,
      errorCode: opaque ? 'cross_origin_script_error' : (event.error?.name ?? 'window_error'),
      severity: opaque ? 'warning' : 'error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: string; stack?: string; name?: string };
    void logError({
      surface: window.location.pathname,
      message: reason?.message ?? String(reason),
      stack: reason?.stack,
      errorCode: reason?.name ?? 'unhandled_rejection',
    });
  });
}

/* ─── httpsCallable wrapper ──────────────────────────────────────── */

/**
 * Drop-in replacement for `httpsCallable(functions, name)` that logs every
 * failure to /errorLog before rethrowing. Use at the module boundary so
 * every callable in the codebase is covered.
 */
export function httpsCallableWithLog<TInput = unknown, TOutput = unknown>(
  functions: Functions,
  name: string,
  options?: HttpsCallableOptions,
): HttpsCallable<TInput, TOutput> {
  const inner = fbHttpsCallable<TInput, TOutput>(functions, name, options);
  const wrapped = (async (data?: TInput | null) => {
    addBreadcrumb('callable', name);
    try {
      const result = await inner(stripUndefined(data) as TInput);
      announceCallableSuccess(name);
      return result;
    } catch (err) {
      const e = err as { code?: string; message?: string; stack?: string; name?: string };
      void logError({
        surface: `callable:${name}`,
        callableName: name,
        message: e.message ?? String(err),
        stack: e.stack,
        errorCode: e.code ?? e.name ?? 'callable_failed',
        inputSnapshot: data,
      });
      throw err;
    }
  }) as unknown as HttpsCallable<TInput, TOutput>;
  (wrapped as unknown as { stream: typeof inner.stream }).stream = inner.stream.bind(inner);
  return wrapped;
}
